import { useQuery } from '@tanstack/react-query';
import { MainMenuOptions, Widgets } from '@types';
import { instance } from '@utils/fetch';
import { auth, config } from '../config';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { fetchWidgetAsync } from '@store/actions/widgets.actions';
import { get, isEmpty } from 'lodash';
import { CodingConstants } from '@constants';
import { clearCookie } from '@utils/cookieHelper';
import { setInitialUnreadCount } from '@store/actions/unreadCount.actions';

type useWidgetsQueryProps = Partial<{
    accountDetailSSO?: (url: string) => Promise<void>;
}>;
export const useWidgetsQuery = ({ accountDetailSSO }: useWidgetsQueryProps) => {
    const dispatch = useDispatch();
    const query = useQuery<Widgets, any>({
        queryKey: ['widgets'],
        queryFn: async () =>
            await instance.request({
                url: `${config.env('awsUrl')}/api/v1/selfserv/customers/widgets?refresh=true`,
                method: 'get'
            }),
        select: ({ data }: any) => data,
        onSuccess: async (widgets: Widgets) => {
            if (widgets) {
                const { redirect, profile } = get({ widgetsData: widgets }, ['widgetsData'], {
                    redirect: { appName: '', url: '' },
                    profile: null
                });
                const getMsgCenterWidgets = widgets?.mainMenuOptions?.find((options: MainMenuOptions) => options?.id === 'Message_Center');
                if (getMsgCenterWidgets) {
                    const unReadMessagesCount = getMsgCenterWidgets?.initParam?.[0]?.unreadCount;
                    dispatch(setInitialUnreadCount(unReadMessagesCount));
                }
                if (!isEmpty(redirect.appName)) {
                    if (redirect.appName.toLowerCase() === CodingConstants.accountConnect.toLowerCase())
                        if (accountDetailSSO) {
                            await accountDetailSSO(`${config.env('myLoanBaseUrl')}/LoanService/SSOPost`);
                            return;
                        } else if (redirect.appName.toLowerCase() === CodingConstants.myFreedomPortal.toLowerCase()) {
                            auth.getOkta().tokenManager.clear();
                            clearCookie();
                            window.location.replace(redirect.url);
                            return;
                        }
                }

                if (!profile) {
                    if (accountDetailSSO) await accountDetailSSO(`${config.env('myLoanBaseUrl')}/LoanService/SSOPost`);
                    return;
                }
            }
        },
        onError: async () => {
            await accountDetailSSO(`${config.env('myLoanBaseUrl')}/LoanService/SSOPost`);
        },
        staleTime: Infinity
    });

    useEffect(() => {
        if (query.isSuccess) {
            dispatch(fetchWidgetAsync.success(query.data));
        } else if (query.isError) {
            dispatch(fetchWidgetAsync.failure(query.error));
        }
    }, [query.isSuccess, query.isError]);

    return query;
};
